import { applyMiddleware, combineReducers, createStore } from "redux";
import { composeWithDevTools } from "redux-devtools-extension";
import createSagaMiddleware from "redux-saga";
import { fork } from "redux-saga/effects";

import * as agent from "./agent";
import * as apiConnectAgent from "./api-connect/agents";
import * as apiConnectCall from "./api-connect/call";
import * as apiConnectCallLog from "./api-connect/callLog";
import * as apiConnectDomain from "./api-connect/domain";
import * as apiConnectExtension from "./api-connect/extension";
import * as apiConnectGroup from "./api-connect/group";
import * as apiConnectIvr from "./api-connect/ivr";
import * as apiConnectPhoneNumber from "./api-connect/phoneNumber";
import * as app from "./app";
import * as auth from "./authentication";
import * as campaign from "./campaigns";
import * as action from "./cloudPBX/action";
import * as callFlow from "./cloudPBX/callFlow";
import * as callFlowNumber from "./cloudPBX/callflowNumber";
import * as channel from "./cloudPBX/channel";
import * as clickToCall from "./cloudPBX/clickToCall";
import * as dialplan from "./cloudPBX/dialplan";
import * as extension from "./cloudPBX/extension";
import * as inboundCall from "./cloudPBX/inboundCall";
import * as ivr from "./cloudPBX/ivr";
import * as livechat from "./cloudPBX/livechat";
import * as outBoundCall from "./cloudPBX/outboundCall";
import * as partnerSwitchboard from "./cloudPBX/partnerSwitchboard";
import * as phoneNumber from "./cloudPBX/phoneNumber";
import * as queue from "./cloudPBX/queue";
import * as sound from "./cloudPBX/sound";
import * as timeRing from "./cloudPBX/timeRing";
import * as webrtc from "./cloudPBX/webrtc";
import * as workTime from "./cloudPBX/workTime";
import * as agentLog from "./contactCenter/agentLog";
import * as agentStatus from "./contactCenter/agentStatus";
import * as dashboard from "./dashboard";
import * as account from "./miniCrm/account";
import * as article from "./miniCrm/article";
import * as comment from "./miniCrm/comment";
import * as contact from "./miniCrm/contact";
import * as indastry from "./miniCrm/indastry";
import * as knowledgeBase from "./miniCrm/knowledgeBase";
import * as monitor from "./miniCrm/monitor";
import * as stage from "./miniCrm/stage";
import * as ticket from "./miniCrm/ticket";
import * as topic from "./miniCrm/topic";
import * as vnpost from "./miniCrm/vnpost";
import * as notification from "./notification";
import * as profiling from "./profiling/data";
import * as profilingInfo from "./profiling/info";
import * as reportAgent from "./report/agent";
import * as callGeneratorLog from "./report/callGeneratorLog";
import * as reportCampaign from "./report/campaign";
import * as configureBillingTime from "./report/configureBillingTime";
import * as reportGroup from "./report/group";
import * as reportInbound from "./report/inbound";
import * as reportOutbound from "./report/outbound";
import * as reportExtension from "./report/reportExtension";
import * as reportQuantity from "./report/reportQuantity";
import * as reportTicket from "./report/reportTicket";
import * as ringmeOttSdk from "./ringme-ott-sdk";
import * as roles from "./roles";
import * as call from "./telesale/call";
import * as form from "./telesale/form";
import * as question from "./telesale/question";
import * as user from "./user";

const reducers = combineReducers({
  app: app.reducer,
  notification: notification.reducer,
  auth: auth.authenticationReducer,
  user: user.userReducer,
  campaign: campaign.campaignsReducer,
  contact: contact.contactsReducer,
  agent: agent.agentReducer,
  account: account.accountsReducer,
  indastry: indastry.indastriesReducer,
  stage: stage.stagesReducer,
  form: form.formsReducer,
  question: question.questionsReducer,
  extension: extension.extensionsReducer,
  partnerSwitchboard: partnerSwitchboard.partnerSwitchboardReducer,
  phoneNumber: phoneNumber.phoneNumberReducer,
  outBoundCall: outBoundCall.outBoundReducer,
  queue: queue.queueReducer,
  call: call.callReducer,
  agentStatus: agentStatus.agentStatusReducer,
  knowledgeBase: knowledgeBase.knowledgeBasesReducer,
  topic: topic.topicReducer,
  article: article.articlesReducer,
  comment: comment.commentReducer,
  inboundCall: inboundCall.inboundReducer,
  agentLog: agentLog.agentLogsReducer,
  dialplan: dialplan.dialplanReducer,
  ivr: ivr.ivrReducer,
  action: action.actionReducer,
  channel: channel.channelReducer,
  reportExtension: reportExtension.reportExtensionReducer,
  reportInbound: reportInbound.inboundReducer,
  reportOutbound: reportOutbound.reportOutboundReducer,
  reportCampaign: reportCampaign.campaignReducer,
  reportAgent: reportAgent.agentReducer,
  reportGroup: reportGroup.reportGroupReducer,
  roles: roles.rolesReducer,
  profiling: profiling.profilingReducer,
  profilingInfo: profilingInfo.profilingInfoReducer,
  apiConnectAgent: apiConnectAgent.agentsReducer,
  apiConnectCall: apiConnectCall.callsReducer,
  apiConnectCallLog: apiConnectCallLog.callLogsReducer,
  apiConnectDomain: apiConnectDomain.domainsReducer,
  apiConnectExtension: apiConnectExtension.extensionsReducer,
  apiConnectGroup: apiConnectGroup.groupsReducer,
  apiConnectIvr: apiConnectIvr.ivrsReducer,
  apiConnectPhoneNumber: apiConnectPhoneNumber.phoneNumberReducer,
  timeRing: timeRing.timeRingReducer,
  webrtc: webrtc.webrtcReducer,
  monitor: monitor.monitorRealtimeReducer,
  sound: sound.soundReducer,
  clickToCall: clickToCall.clickToCallReducer,
  callGeneratorLog: callGeneratorLog.callGeneratorLogReducer,
  dashboard: dashboard.dashboardReducer,
  ticket: ticket.ticketReducer,
  vnpost: vnpost.packagesReducer,
  reportTicket: reportTicket.reportTickerReducer,
  reportQuantity: reportQuantity.reportQuantityReducer,
  configureBillingTime: configureBillingTime.configureBillingTimeReducer,
  workTime: workTime.worktimeReducer,
  callFlow: callFlow.callflowReducer,
  callFlowNumber: callFlowNumber.callflowNumberReducer,
  livechat: livechat.ConversationHistoryReducer,
  ringmeOttSdk: ringmeOttSdk.ringmeOttSdkReducer,
});

const sagas = function* () {
  yield [
    fork(auth.initFlow),
    fork(auth.checkStatusFlow),
    fork(auth.loginFlow),
    fork(user.getInfoFlow),
    fork(user.getAllUserFlow),
    fork(user.addUserFlow),
    fork(user.editUserFlow),
    fork(user.deleteUserFlow),
    fork(user.countUserFlow),
    fork(user.logoutFlow),
    fork(user.getUserHistoryFlow),
    fork(user.countUserHistoryFlow),
    fork(user.getAllUserHistoryFlow),
    fork(user.getSubOrgFlow),
    fork(user.findProvinceFlow),
    // fork(user.getProfileFlow),
    fork(user.changePasswordFlow),
    fork(user.changeProfileFlow),
    //role
    fork(roles.getRoleFlow),
    fork(roles.countRoleFlow),
    // fork(roles.addRoleFlow),
    // fork(roles.editRoleFlow),
    // fork(roles.deleteRoleFlow),
    //campaign
    fork(campaign.getListCampaignsFlow),
    fork(campaign.checkCampaignGroupCodeFlow),
    fork(campaign.checkCampaignServiceCodeFlow),
    fork(campaign.getAllCampaignsFlow),
    fork(campaign.countCampaignsFlow),
    fork(campaign.addNewCampaignsFlow),
    fork(campaign.editCampaignsFlow),
    fork(campaign.reloadCampaignsFlow),
    fork(campaign.deleteCampaignsFlow),
    fork(campaign.getACampaignDetailFow),
    fork(campaign.campaignConfigFlow),
    fork(campaign.getCampaignAllocationFlow),
    fork(campaign.countCampaignsAllocationFlow),
    fork(campaign.getMoreDataFlow),
    fork(campaign.getReasonSelectFlow),
    fork(campaign.getListCampaignsGroupFlow),
    fork(campaign.countCampaignsGroupFlow),
    fork(campaign.addNewCampaignsGroupFlow),
    fork(campaign.editCampaignsGroupFlow),
    fork(campaign.deleteCampaignsGroupFlow),
    fork(campaign.getListCampaignsServiceFlow),
    fork(campaign.countCampaignsServiceFlow),
    fork(campaign.addNewCampaignsServiceFlow),
    fork(campaign.editCampaignsServiceFlow),
    fork(campaign.deleteCampaignsServiceFlow),
    //contact
    fork(contact.getListContactsFlow),
    fork(contact.addNewContactsFlow),
    fork(contact.deleteContactsFlow),
    fork(contact.editContactsFlow),
    fork(contact.countContactsFlow),
    fork(contact.getContactDetailFlow),
    //contact file
    fork(contact.getListContactFileFlow),
    fork(contact.addNewContactsFileFlow),
    fork(contact.deleteContactsFileFlow),
    fork(contact.editContactsFileFlow),
    fork(contact.countContactFileFlow),
    fork(contact.countListContactFileFlow),
    //agent
    fork(agent.getListAgentFlow),
    fork(agent.countAgentFlow),
    fork(agent.addNewAgentFlow),
    fork(agent.editAgentFlow),
    fork(agent.deleteAgentFlow),
    fork(agent.getAAgentFlow),
    // fork(agent.getListStatusAgentFlow),
    fork(agent.getStatusAgentFlow),
    fork(agent.changeStatusAgentFlow),
    fork(agent.workingInfoFlow),
    fork(agent.handleEndActiveCallFlow),
    fork(agent.updateLastStatusFlow),
    fork(agent.getLogAgentFlow),
    fork(agent.getAgentSessionFlow),
    //group agent
    fork(agent.getListGroupAgentFlow),
    fork(agent.countGroupAgentFlow),
    fork(agent.addNewGroupAgentFlow),
    fork(agent.editGroupAgentFlow),
    fork(agent.deleteGroupAgentFlow),
    //account
    fork(account.getListAccountFlow),
    fork(account.countAccountFlow),
    fork(account.addNewAccountFlow),
    fork(account.editAccountFlow),
    fork(account.deleteAccountFlow),
    //indastry
    fork(indastry.getListIndastryFlow),
    //stage
    fork(stage.getListStageFlow),
    fork(stage.countStageFlow),
    fork(stage.addNewStageFlow),
    fork(stage.editStageFlow),
    fork(stage.deleteStageFlow),
    //form
    fork(form.getListFormFlow),
    fork(form.countFormFlow),
    fork(form.addNewFormFlow),
    fork(form.deleteFormFlow),
    fork(form.editFormFlow),
    fork(form.getAFormFlow),
    //question
    fork(question.getListQuestionFlow),
    fork(question.countQuestionFlow),
    //extension
    fork(extension.getListExtensionFlow),
    fork(extension.deleteExtensionFlow),
    fork(extension.addExtensionFlow),
    fork(extension.editExtensionFlow),
    fork(extension.countExtensionFlow),
    //partnerSwitchboard
    fork(partnerSwitchboard.getListPartnerSwitchBoardFlow),
    fork(partnerSwitchboard.deletePartnerSwitchBoardFlow),
    fork(partnerSwitchboard.addPartnerSwitchBoardFlow),
    fork(partnerSwitchboard.editPartnerSwitchBoardFlow),
    fork(partnerSwitchboard.countPartnerSwitchBoardFlow),
    //phoneNumbner
    fork(phoneNumber.getListFlow),
    fork(phoneNumber.countNumberFlow),
    fork(phoneNumber.getNumberDefaultFlow),
    fork(phoneNumber.setNumberDefaultFlow),
    fork(phoneNumber.editNumberFlow),
    //outboundCall
    fork(outBoundCall.getOutboundCallFlow),
    fork(outBoundCall.getOutboundCallAgentFlow),
    fork(outBoundCall.countOutBoundCallFlow),
    fork(outBoundCall.addNoteOutboundCallApiFlow),
    //inbound call
    fork(inboundCall.getListInboundCallFlow),
    fork(inboundCall.getInboundCallAgentFlow),
    fork(inboundCall.countInboundCallFlow),
    //queue
    fork(queue.getListQueueFlow),
    fork(queue.countQueueFlow),
    fork(queue.addQueueFlow),
    fork(queue.editQueueFlow),
    fork(queue.deleteQueueFlow),
    //call
    fork(call.getListCallFlow),
    fork(call.findPostOfficeFlow),
    fork(call.findHandlePeopleFlow),
    fork(call.makeACallFlow),
    fork(call.countFlow),
    fork(call.getReCallLogFlow),
    fork(call.getArticleLogFlow),
    fork(call.changeStateInteractiveLogFlow),
    fork(call.editInteractiveLogFlow),
    fork(call.getInfoTelesaleFlow),
    fork(call.updateFormFlow),
    fork(call.exportInteractiveLogFlow),
    fork(call.getInteractiveLogFlow),
    fork(call.getTodayMixCallsFlow),
    fork(call.getInfoCustomerFlow),
    fork(call.getInfoPhoneNumberFlow),
    fork(call.getInfoCustomerWithoutSocketFlow),
    fork(call.getTicketByNumberFlow),
    fork(call.updateTicketByNumberFlow),
    fork(call.addNoteCallFlow),
    fork(call.getMixCallsFlow),
    fork(call.addNewCustomerFlow),
    fork(call.requestBHXHFlow),
    fork(call.openDialogPackageDetailFlow),
    fork(call.getServicesFlow),
    fork(call.getAgentReportWorkingFlow),
    fork(call.findPosFlow),
    fork(call.findLeadFlow),
    fork(call.findAccountFlow),
    fork(call.getReferenceResourceFlow),
    //agent status
    fork(agentStatus.getListAgentStatusFlow),
    fork(agentStatus.countAgentStatusFlow),
    fork(agentStatus.addNewAgentStatusFlow),
    fork(agentStatus.editAgentStatusFlow),
    fork(agentStatus.deleteAgentStatusFlow),
    //knowledgeBase
    fork(knowledgeBase.getListKnowledgeBaseFlow),
    fork(knowledgeBase.countKnowledgeBaseFlow),
    fork(knowledgeBase.addNewKnowledgeBaseFlow),
    fork(knowledgeBase.editKnowledgeBaseFlow),
    fork(knowledgeBase.deleteKnowledgeBaseFlow),
    fork(knowledgeBase.searchKnowledgeBaseFlow),
    //topic
    fork(topic.getListTopicFlow),
    fork(topic.countTopicFlow),
    fork(topic.addNewTopicFlow),
    fork(topic.editTopicFlow),
    fork(topic.deleteTopicFlow),
    fork(topic.getDataTreeFlow),
    //article
    fork(article.getListArticleFlow),
    fork(article.countArticleFlow),
    fork(article.addNewArticleFlow),
    fork(article.editArticleFlow),
    fork(article.deleteArticleFlow),
    fork(article.openAArticleFlow),
    fork(article.searchArticleFlow),
    //comemt
    fork(comment.getListCommentFlow),
    fork(comment.countCommentFlow),
    fork(comment.addNewCommentFlow),
    fork(comment.editCommentFlow),
    fork(comment.deleteCommentFlow),
    //agentLog
    fork(agentLog.getListAgentLogsFlow),
    fork(agentLog.countAgentLogsFlow),
    fork(agentLog.addNewAgentLogsFlow),
    fork(agentLog.deleteAgentLogsFlow),
    fork(agentLog.editAgentLogsFlow),
    //dialplan
    fork(dialplan.getListDialplanFlow),
    fork(dialplan.countDialplanFlow),
    fork(dialplan.addDialplanFlow),
    fork(dialplan.editDialplanFlow),
    fork(dialplan.deleteDialplanFlow),
    //ivr
    fork(ivr.getListIvrFlow),
    fork(ivr.countIvrFlow),
    fork(ivr.addIvrFlow),
    fork(ivr.editIvrFlow),
    fork(ivr.deleteIvrFlow),
    //action
    fork(action.getListActionFlow),
    //sound
    fork(sound.getListSoundFlow),
    fork(sound.countSoundFlow),
    fork(sound.addSoundFlow),
    fork(sound.editSoundFlow),
    fork(sound.deleteSoundFlow),
    fork(sound.uploadSoundFlow),
    //channel
    fork(channel.getListChannelFlow),
    fork(channel.countChannelFlow),
    fork(channel.getActiveCallFlow),
    //webrtc
    fork(webrtc.configWebrtcFlow),
    fork(webrtc.callNumberFlow),
    fork(webrtc.answerCallFlow),
    // fork(webrtc.sessionFlow),
    fork(webrtc.endCallFlow),
    //report
    fork(reportExtension.getListReportExtensionFlow),
    fork(reportExtension.getProvinceTypesFlow),
    fork(reportExtension.getExtensionContactCentersFlow),
    fork(reportOutbound.getListReportOutboundFlow),
    fork(reportOutbound.countReportOutboundFlow),
    fork(reportInbound.getListReportInboundFlow),
    fork(reportInbound.countReportInboundFlow),
    fork(reportCampaign.getListReportCampaignFlow),
    fork(reportCampaign.getCampaignReportDetailFow),
    fork(reportCampaign.getListReportCampaignDetailFlow),
    fork(reportCampaign.campaignConnectedRateByHourFlow),
    fork(reportCampaign.campaignReportByHourFlow),
    fork(reportCampaign.campaignReportByAgentFlow),
    fork(reportCampaign.campaignReportByDateFlow),
    fork(reportCampaign.campaingReportByStageFlow),
    fork(reportAgent.getListReportAgentFlow),
    fork(reportAgent.getListReportAgentByHourFlow),
    fork(reportAgent.getListReportAgentOutboundFlow),
    fork(reportAgent.getAgentReportDetailFlow),
    fork(reportAgent.getAgentReportByDateDetailFlow),
    fork(reportAgent.getListReportAgentPerformenceFlow),
    fork(reportAgent.getAgentCallDurationFlow),
    fork(reportAgent.getListReportAgentAndGroupPerformenceFlow),
    fork(reportAgent.reportAgentInboundFlow),
    fork(reportGroup.reportByGroupFlow),
    fork(reportTicket.getListReportTicketFlow),
    fork(reportQuantity.getListReportTicketFlow),
    fork(callGeneratorLog.callGeneratorLogFlow),

    fork(configureBillingTime.getListConfigureBillingTimeFlow),
    fork(configureBillingTime.countListConfigureBillingTimeFlow),
    fork(configureBillingTime.editConfigFlow),
    fork(configureBillingTime.deleteConfigFlow),

    //profiling
    fork(profiling.getListProfilingFlow),
    fork(profiling.countListProfilingFlow),
    fork(profiling.getTagProfilingFlow),
    fork(profiling.getBiographyProfilingFlow),
    fork(profiling.getDataTagsProfilingFlow),
    fork(profiling.getLocationProfilingFlow),
    fork(profilingInfo.getProfilingInfoFlow),
    //api connect
    fork(apiConnectPhoneNumber.getPhoneNumberFlow),
    fork(apiConnectPhoneNumber.putChangePhoneNumberFlow),

    fork(apiConnectIvr.getIvrsFlow),
    fork(apiConnectIvr.deleteAIvrFlow),
    fork(apiConnectIvr.getIvrDetailFlow),
    fork(apiConnectIvr.createAIvrFlow),
    fork(apiConnectIvr.updateAIvrFlow),

    fork(apiConnectGroup.getGroupsFlow),
    fork(apiConnectGroup.updateAGroupFlow),
    fork(apiConnectGroup.createAGroupFlow),
    fork(apiConnectGroup.deleteAGroupFlow),

    fork(apiConnectAgent.getAgentsFlow),
    fork(apiConnectAgent.updateAAgentFlow),
    fork(apiConnectAgent.getAgentDetailFlow),
    fork(apiConnectAgent.deleteAAgentFlow),
    fork(apiConnectAgent.createAAgentFlow),

    fork(apiConnectExtension.getExtensionApiConnectFlow),
    fork(apiConnectExtension.putChangeExtensionFlow),
    fork(apiConnectExtension.createAExtensionFlow),
    fork(apiConnectExtension.deleteAExtensionFlow),
    fork(apiConnectExtension.getExtensionApiConnectOnlineFlow),

    fork(apiConnectCall.getCallsFlow),
    fork(apiConnectCall.makeACallOutFlow),
    fork(apiConnectCall.disconnectCallFlow),
    fork(apiConnectCall.callSpyFlow),

    fork(apiConnectCallLog.postInBoundFlow),
    fork(apiConnectCallLog.postOutBoundFlow),

    fork(apiConnectDomain.putChangeDomainFlow),
    //timeRing
    fork(timeRing.getConfigTimeFlow),
    fork(timeRing.configTimeFlow),
    //monitor
    fork(monitor.monitorRealtimeFlow),
    fork(monitor.spyCallFlow),
    fork(monitor.campaignsRealtimeFlow),
    fork(monitor.agentCallsRealtimeFlow),
    fork(monitor.callsRealtimeFlow),
    fork(clickToCall.clickToCallFlow),
    fork(monitor.getChatAgentsFlow),
    //dashboard
    fork(dashboard.dashboardInboundFlow),
    fork(dashboard.dashboardOutboundFlow),
    fork(dashboard.dashboardContactFlow),
    fork(dashboard.dashboardContractFlow),
    fork(dashboard.getListSourcePageFlow),
    fork(dashboard.getAgentChatReportFlow),

    fork(ticket.getTicketTypeFlow),
    fork(ticket.addNewTicketFlow),
    fork(ticket.updateTicketFlow),
    fork(ticket.getContactSFFlow),
    fork(ticket.getEmployeeSFFlow),
    fork(ticket.getPosSFFlow),
    fork(ticket.getProductSFFlow),
    fork(ticket.getSaleOrderSFFlow),
    fork(ticket.getPackageSFFlow),
    fork(ticket.getListTicketFlow),
    fork(ticket.getReportTicketFlow),
    fork(ticket.countTicketFlow),
    fork(ticket.countReportTicketFlow),
    fork(ticket.countTicketByPhoneFlow),
    //vnpost
    fork(vnpost.getListPackageFlow),
    fork(vnpost.getListPostOfficeFlow),
    fork(vnpost.countPackageFlow),
    fork(vnpost.addNewPackageFlow),
    fork(vnpost.deletePackageFlow),
    fork(vnpost.editPackageFlow),
    //callflow
    fork(callFlow.getListCallflowFlow),
    fork(callFlow.deleteCallflowFlow),
    fork(callFlow.editCallflowFlow),
    fork(callFlow.addCallflowFlow),
    fork(callFlow.countCallflowFlow),
    // workTime
    fork(workTime.getListWorktimeFlow),
    fork(workTime.deleteWorktimeFlow),
    fork(workTime.editWorktimeFlow),
    fork(workTime.addWorktimeFlow),
    fork(workTime.countWorktimeFlow),
    // callflow number
    fork(callFlowNumber.getListCallflowNumberFlow),
    fork(callFlowNumber.deleteCallflowNumberFlow),
    fork(callFlowNumber.editCallflowNumberFlow),
    fork(callFlowNumber.addCallflowNumberFlow),
    fork(callFlowNumber.countCallflowNumberFlow),
    //live chat
    fork(livechat.getConversationFlow),
    fork(livechat.closeConversationFlow),
    fork(livechat.joinConversationFlow),
    fork(livechat.sendMessageFlow),
    fork(livechat.getListConversationHistoryFlow),

    fork(ringmeOttSdk.getListChatTicketHistoryByUsernameFlow),
    fork(ringmeOttSdk.fetchAgentChatStatisticFlow),
    fork(ringmeOttSdk.getCustomerChatHistoryFlow),
    fork(ringmeOttSdk.getRingmeChatReportByHourFlow),
    fork(ringmeOttSdk.getRingmeChatReportByDateFlow),
    fork(ringmeOttSdk.getRingmeListChatQueueIdFlow),
  ];
};

const sagaMiddleware = createSagaMiddleware();

const store = createStore(
  reducers,
  composeWithDevTools(applyMiddleware(sagaMiddleware))
);
sagaMiddleware.run(sagas);

export default store;
